// Copyright © 2023 Move Closer
import { __decorate } from "tslib";
import { Component, Prop, Vue } from 'vue-property-decorator';
import videoJS from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';
// import { imaPlugin } from '../../../../../types/services/ima'
import { BootstrapLink } from '../../../../dsl/bootstrap';
import { loadScript } from '../../../../support';
import { PlayerState } from '../../../_contracts';
let VideoPlayerFile = class VideoPlayerFile extends Vue {
    constructor() {
        super(...arguments);
        this.isVideoPlaying = false;
        this.player = null;
        this.videoOptions = { muted: true };
        this.baseImaOptions = {
            adsRenderingSettings: { enablePreloading: true },
            contribAdsSettings: { debug: true },
            debug: true,
            disableCustomPlaybackForIOS10Plus: true,
            forceNonLinearFullSlot: true,
            locale: 'pl'
        };
        this.isInitialized = false;
        this.isImaPluginLoaded = false;
        this.eventHandlers = [];
        this.handlePlay = () => this.handleEvent(PlayerState.Playing);
        this.handlePause = () => this.handleEvent(PlayerState.Paused);
        this.handleStop = () => this.handleEvent(PlayerState.Ended);
    }
    get imaOptions() {
        return {
            ...this.baseImaOptions,
            adTagUrl: this.videoAdsTagPrepared
        };
    }
    get videoAdsTagPrepared() {
        return this.videoAdsTag ? (this.videoAdsTag
            .replace('description_url=[placeholder]', `description_url=${encodeURIComponent(window.location.href)}`)
            .replace('cmsid=[placeholder]', `cmsid=${this.cmsId}}`)
            .replace('vid=[placeholder]', `vid=${this.videoId}`)) : undefined;
    }
    get isHLS() {
        return !!this.video && ('m3u8' in this.video) && !!this.video.m3u8;
    }
    get cmsId() {
        return '';
    }
    get videoId() {
        if (!this.video) {
            return '';
        }
        return this.video.id;
    }
    get url() {
        if (!this.video) {
            return '';
        }
        return this.video.m3u8 ? this.video.m3u8 : this.video.url;
    }
    get type() {
        if (!this.video) {
            return '';
        }
        return this.isHLS ? 'application/x-mpegURL' : 'video/' + this.video.mime;
    }
    get videoMetadata() {
        return {
            ...this.baseVideoMetadata,
            embedUrl: this.url
        };
    }
    onPlay(event) {
        if (typeof event.payload === 'undefined') {
            return;
        }
        const { playerId } = event.payload;
        if (playerId !== this.videoAttrId) {
            return;
        }
        if (this.isImaPluginLoaded && !this.isInitialized) {
            this.initImaPlugin();
        }
        this.$nextTick(() => {
            this.$emit('init');
            setTimeout(async () => {
                if (!this.player) {
                    return;
                }
                this.player.play();
            }, 200);
        });
    }
    onPause(event) {
        if (typeof event.payload === 'undefined') {
            return;
        }
        const { playerId } = event.payload;
        if (playerId !== this.videoAttrId) {
            return;
        }
        if (this.player) {
            this.player.pause();
        }
    }
    onStateChanged(event) {
        if (!this.player || typeof event.payload === 'undefined') {
            return;
        }
        const { playerId, state } = event.payload;
        if (playerId === this.videoAttrId) {
            return;
        }
        if (state === PlayerState.Playing) {
            this.player.pause();
        }
    }
    watchEvents() {
        if (!this.eventBus) {
            return;
        }
        this.eventHandlers = [
            this.eventBus.handle('ui:video-player.play', this.onPlay),
            this.eventBus.handle('ui:video-player.pause', this.onPause),
            this.eventBus.handle('ui:video-player.state-changed', this.onStateChanged)
        ];
    }
    clearEvents() {
        this.eventHandlers.forEach(handler => handler.unsubscribe());
    }
    async initPlayer() {
        if (typeof window === 'undefined' || this.player) {
            return;
        }
        this.isHLS ? await this.initVideoJS() : this.initNativePlayer();
    }
    initNativePlayer() {
        this.player = this.$refs.video;
        this.player.addEventListener('play', this.handlePlay);
        this.player.addEventListener('pause', this.handlePause);
        this.player.addEventListener('ended', this.handleStop);
    }
    async initVideoJS() {
        this.isImaPluginLoaded = false;
        try {
            await loadScript('//imasdk.googleapis.com/js/sdkloader/ima3.js');
            this.player = videoJS(this.$refs.video, this.videoOptions);
            if ('ima' in this.player && typeof this.player.ima === 'function') {
                this.player.ima(this.imaOptions);
            }
            this.isImaPluginLoaded = true;
        }
        catch (e) {
            console.warn('[EmbedVideo] Failed to load GoogleAds scripts.', e);
            this.player = videoJS(this.$refs.video, this.videoOptions);
        }
        if (this.player) {
            this.player.on('play', () => this.handleEvent(PlayerState.Playing));
            this.player.on('pause', () => this.handleEvent(PlayerState.Paused));
            this.player.on('ended', () => this.handleEvent(PlayerState.Ended));
        }
    }
    initImaPlugin() {
        if (!this.ads) {
            return;
        }
        if (!(this.player && 'ima' in this.player && typeof this.player.ima === 'function')) {
            return;
        }
        this.player.ima.initializeAdDisplayContainer();
        this.player.ima.setContentWithAdTag(null, this.imaOptions.adTagUrl, false);
        this.player.ima.requestAds();
        this.isInitialized = true;
    }
    handleEvent(state) {
        const payload = {
            playerId: this.videoAttrId,
            state
        };
        if (this.eventBus) {
            this.eventBus.emit('ui:video-player.state-changed', payload);
        }
        switch (state) {
            case PlayerState.Playing:
                this.$emit('play');
                break;
            case PlayerState.Paused:
            case PlayerState.Ended:
                this.$emit('pause');
                break;
        }
    }
};
__decorate([
    Prop({
        type: Object,
        required: true
    })
], VideoPlayerFile.prototype, "baseVideoMetadata", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], VideoPlayerFile.prototype, "eventBus", void 0);
__decorate([
    Prop({
        type: String,
        required: true
    })
], VideoPlayerFile.prototype, "videoAttrId", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], VideoPlayerFile.prototype, "videoAdsTag", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], VideoPlayerFile.prototype, "videoClass", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], VideoPlayerFile.prototype, "ads", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], VideoPlayerFile.prototype, "video", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], VideoPlayerFile.prototype, "shouldInit", void 0);
VideoPlayerFile = __decorate([
    Component({
        name: 'VideoPlayerFile',
        components: { BootstrapLink },
        async mounted() {
            // console.log('BeforeInitPlayer')
            await this.initPlayer();
            this.watchEvents();
        },
        beforeDestroy() {
            if (this.player) {
                if ('dispose' in this.player) {
                    this.player.dispose();
                }
                if ('removeEventListener' in this.player) {
                    this.player.removeEventListener('play', this.handlePlay);
                    this.player.removeEventListener('pause', this.handlePause);
                    this.player.removeEventListener('ended', this.handleStop);
                }
            }
            this.player = null;
            this.clearEvents();
        },
        template: `
    <div class="embed-video__player__wrapper"
         :class="{ 'embed-video__player__wrapper--native': !isHLS, 'embed-video__player__wrapper--vjs': isHLS }">
    <component :is="'script'" type="application/ld+json" v-html="JSON.stringify(videoMetadata)"/>

    <video ref="video" controls playsinline webkit-playsinline class="embed-video__player"
           :class="[videoClass, { 'video-js': isHLS }]">
      <source :src="url" :type="type">
      <p>
        {{ $t('modules.embed.videoNotSupport') }}
        <BootstrapLink :link="{ target: url, label: $t('modules.embed.videoLink') }"/>
      </p>
    </video>
    </div>
  `
    })
], VideoPlayerFile);
export { VideoPlayerFile };
export default VideoPlayerFile;
